// ============================================================
// ELC Shift — Uploads / Daily Upload (unified sales + addon) module
//
// One xlsx workbook with multiple sheets — "Daily Sales by Store"
// plus per-category sheets ("Demo", "Batteries", "Gift Wrap",
// "Reusable Bag", "Gift Cards"). Sales sheet is currency values;
// addon sheets are integer counts. Missing tabs are skipped, so
// partial uploads work (e.g. "just Sales today, add Demo column
// tomorrow").
//
// On Save: sales rows go to DB.sales.upload, addon rows go to
// DB.addon_actuals.upload, in parallel. Independent failure of
// one half doesn't fail the other — admin sees per-half status.
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.daily.handleDrop(event)             — file dropzone drop
//   Uploads.daily.handleFile(file)              — programmatic file input
//   Uploads.daily.acceptSalesSuggestion(...)    — "Did you mean...?" sales
//   Uploads.daily.dismissSalesSuggestion(store) — "Did you mean...?" sales
//   Uploads.daily.acceptAddonSuggestion(...)    — "Did you mean...?" addon
//   Uploads.daily.dismissAddonSuggestion(store) — "Did you mean...?" addon
//   Uploads.daily.save()                        — parallel upsert to DB
//   Uploads.daily.clearPreview()                — wipe preview UI
//   Uploads.daily.downloadTemplate()            — generate empty 6-sheet xlsx
//
// Free globals (referenced as window.foo):
//   - XLSX                  (SheetJS)
//   - buildUploadNameMap    (admin.html helper)
//   - escapeHtml            (admin.html escape utility)
//   - ALL_STORES            (admin.html canonical store list)
//   - staffData             (admin.html staff list)
//   - getStoreType          (admin.html — returns 'virtual' for relief)
//   - getStoreUploadName    (admin.html — returns xlsx column name)
//   - DB                    (db.js DB facade)
//   - showToast             (admin.html toast helper)
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import {
  parseMatrixSheet,
  findStoreColumnSuggestions,
  renderStoreSuggestionCards,
} from './shared.js';

// Module state — fresh on every parsed file.
let dailyUploadParseState = null;

const DAILY_SALES_SHEET_LC = 'daily sales by store';

// Mirrors ADDON_SHEET_FIELDS in addon.js but kept as a local list so
// this code is self-contained and easier to read.
const DAILY_ADDON_SHEETS = [
  { sheet: 'demo',         field: 'demo_units' },
  { sheet: 'batteries',    field: 'batteries' },
  { sheet: 'gift wrap',    field: 'gift_wrap' },
  { sheet: 'reusable bag', field: 'reusable_bag' },
  { sheet: 'gift cards',   field: 'gift_cards' },
];

export function handleDrop(e) {
  e.preventDefault();
  if (e.currentTarget && e.currentTarget.style) {
    e.currentTarget.style.borderColor = 'var(--border)';
    e.currentTarget.style.background = 'var(--surface-2)';
  }
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
}

export function handleFile(file) {
  if (!file || !file.name.endsWith('.xlsx')) {
    showDailyUploadStatus('Please upload an .xlsx file', 'error'); return;
  }
  dailyUploadParseState = null;
  showDailyUploadStatus('Reading ' + file.name + '…', 'info');
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { handleFile(file); }, function (e) { showDailyUploadStatus(e.message, 'error'); }); return; }
  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const wb = XLSX.read(e.target.result, {type:'array', cellDates:true});
      // Pre-extract per-sheet rows; build store-name map ONCE.
      const dynamicMap = buildUploadNameMap();
      const storeMapUpper = {};
      Object.entries(dynamicMap).forEach(([k,v]) => { storeMapUpper[k.trim().toUpperCase()] = v; });

      const sheetByName = {};
      wb.SheetNames.forEach(n => { sheetByName[n.toLowerCase()] = n; });

      const salesSheetName = sheetByName[DAILY_SALES_SHEET_LC];
      const salesRows = salesSheetName
        ? XLSX.utils.sheet_to_json(wb.Sheets[salesSheetName], {header:1, raw:false, dateNF:'yyyy-mm-dd'})
        : null;

      const addonRows = {};   // {field: rows[]}
      DAILY_ADDON_SHEETS.forEach(({sheet, field}) => {
        const realName = sheetByName[sheet];
        if (realName) {
          addonRows[field] = XLSX.utils.sheet_to_json(wb.Sheets[realName], {header:1, raw:false, dateNF:'yyyy-mm-dd'});
        }
      });

      dailyUploadParseState = {
        salesRows, addonRows, storeMapUpper,
        salesAccepted: [], salesDismissed: new Set(),
        addonAccepted: [], addonDismissed: new Set(),
        filename: file.name,
        // Computed on rebuild and stashed for the Save button:
        flatSales: null, flatAddons: null,
      };
      rebuildFromState();
    } catch (err) {
      showDailyUploadStatus('Error reading file: ' + err.message, 'error');
      console.error('[daily-upload]', err);
    }
  };
  reader.readAsArrayBuffer(file);
}

function rebuildFromState() {
  if (!dailyUploadParseState) return;
  const st = dailyUploadParseState;

  // ── Parse sales sheet, if present ──
  let flatSales = null;
  let salesUnmatched = [];
  let salesError = null;
  if (st.salesRows) {
    const parsed = parseMatrixSheet(st.salesRows, st.storeMapUpper, 'Date', st.salesAccepted);
    if (!parsed.ok) {
      salesError = parsed.error;
    } else {
      flatSales = [];
      Object.entries(parsed.byStoreDate).forEach(([store, dates]) => {
        Object.entries(dates).forEach(([date, value]) => {
          flatSales.push({ store, date, sales: Number(value) });
        });
      });
      salesUnmatched = parsed.unmatchedHeaders || [];
    }
  }

  // ── Parse addon sheets, dedup unmatched headers across them ──
  const addonMerged = {};   // {store: {date: {field: count}}}
  const addonSheetsFound = [];
  const addonSheetsMissing = [];
  const addonUnmatchedByText = new Map();
  DAILY_ADDON_SHEETS.forEach(({sheet, field}) => {
    if (!st.addonRows[field]) { addonSheetsMissing.push(sheet); return; }
    addonSheetsFound.push(sheet);
    const headerRow = st.addonRows[field][findHeaderRow(st.addonRows[field])] || [];
    const extrasForThisSheet = st.addonAccepted.map(e => {
      const idx = headerRow.findIndex(h => String(h || '').trim() === e.header);
      return idx >= 0 ? { idx, appName: e.appName } : null;
    }).filter(Boolean);
    const parsed = parseMatrixSheet(st.addonRows[field], st.storeMapUpper, 'Date', extrasForThisSheet);
    if (!parsed.ok) { addonSheetsMissing.push(sheet + ' (' + parsed.error + ')'); return; }
    Object.entries(parsed.byStoreDate).forEach(([store, dates]) => {
      if (!addonMerged[store]) addonMerged[store] = {};
      Object.entries(dates).forEach(([date, value]) => {
        if (!addonMerged[store][date]) addonMerged[store][date] = {};
        addonMerged[store][date][field] = Math.round(Number(value));
      });
    });
    (parsed.unmatchedHeaders || []).forEach(h => {
      if (!addonUnmatchedByText.has(h.header)) addonUnmatchedByText.set(h.header, h);
    });
  });
  const flatAddons = [];
  Object.entries(addonMerged).forEach(([store, dates]) => {
    Object.entries(dates).forEach(([date, fields]) => {
      flatAddons.push(Object.assign({ store, date }, fields));
    });
  });
  const addonUnmatched = Array.from(addonUnmatchedByText.values());

  // Stash for Save
  st.flatSales  = flatSales;
  st.flatAddons = flatAddons;

  // ── Compute "Did you mean" suggestions for both sections ──
  const expectedStores = Array.isArray(ALL_STORES)
    ? ALL_STORES.filter(s => s && s !== 'Retail Relief Managers')
    : [];
  const salesMatched = new Set((flatSales || []).map(r => r.store));
  const addonMatched = new Set((flatAddons || []).map(r => r.store));
  const salesMissing = expectedStores.filter(s => !salesMatched.has(s));
  const addonMissing = expectedStores.filter(s => !addonMatched.has(s));
  const salesSuggestions = findStoreColumnSuggestions(salesUnmatched, salesMissing, st.salesDismissed);
  const addonSuggestions = findStoreColumnSuggestions(addonUnmatched, addonMissing, st.addonDismissed);

  // ── Build preview HTML ──
  let html = '';

  // Top summary
  let summary = '';
  if (st.salesRows) {
    if (salesError) summary += '⚠ Sales sheet error: ' + escapeHtml(salesError) + '. ';
    else summary += '✅ Sales: ' + ((flatSales || []).length) + ' rows. ';
  } else {
    summary += '— Sales sheet not in file. ';
  }
  if (addonSheetsFound.length) summary += '✅ Add-on sheets: ' + addonSheetsFound.join(', ') + ' (' + flatAddons.length + ' rows). ';
  if (addonSheetsMissing.length) summary += 'Skipped: ' + addonSheetsMissing.join(', ') + '.';
  showDailyUploadStatus(summary || 'No recognised sheets found.', salesError ? 'error' : (flatSales || flatAddons.length ? 'success' : 'error'));

  // Suggestion blocks — namespaced for inline onclick resolution.
  if (salesSuggestions.length) {
    html += '<div style="font-size:11px;color:var(--sub);margin:10px 0 4px;font-weight:700;text-transform:uppercase;letter-spacing:.5px">Sales — Did you mean…?</div>';
    html += renderStoreSuggestionCards(salesSuggestions, 'Uploads.daily.acceptSalesSuggestion', 'Uploads.daily.dismissSalesSuggestion');
  }
  if (addonSuggestions.length) {
    html += '<div style="font-size:11px;color:var(--sub);margin:10px 0 4px;font-weight:700;text-transform:uppercase;letter-spacing:.5px">Add-on KPIs — Did you mean…?</div>';
    html += renderStoreSuggestionCards(addonSuggestions, 'Uploads.daily.acceptAddonSuggestion', 'Uploads.daily.dismissAddonSuggestion');
  }

  // Sample rows for sales
  if (flatSales && flatSales.length) {
    const sample = flatSales.slice(0, 5);
    html += '<div style="margin-top:14px;font-size:11px;color:var(--sub);font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">Sales sample</div>';
    html += '<table style="width:100%;font-size:12px;border-collapse:collapse;margin-bottom:10px">';
    html += '<thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Store</th><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Date</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Sales</th></tr></thead><tbody>';
    sample.forEach(r => {
      html += '<tr><td style="padding:4px 8px">' + escapeHtml(r.store) + '</td><td style="padding:4px 8px">' + escapeHtml(r.date) + '</td><td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + r.sales.toLocaleString(undefined,{maximumFractionDigits:2}) + '</td></tr>';
    });
    html += '</tbody></table>';
  }

  // Sample rows for addons
  if (flatAddons && flatAddons.length) {
    const sample = flatAddons.slice(0, 5);
    html += '<div style="font-size:11px;color:var(--sub);font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">Add-on KPIs sample</div>';
    html += '<table style="width:100%;font-size:12px;border-collapse:collapse">';
    html += '<thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Store</th><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Date</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Demo</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Bat</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Wrap</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Bag</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Cards</th></tr></thead><tbody>';
    sample.forEach(r => {
      html += '<tr><td style="padding:4px 8px">' + escapeHtml(r.store) + '</td><td style="padding:4px 8px">' + escapeHtml(r.date) + '</td>';
      ['demo_units','batteries','gift_wrap','reusable_bag','gift_cards'].forEach(f => {
        html += '<td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + (r[f] != null ? r[f] : '—') + '</td>';
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
  }

  const previewCard = document.getElementById('dailyUploadPreviewCard');
  if (previewCard) previewCard.style.display = 'block';
  const previewEl = document.getElementById('dailyUploadPreview');
  if (previewEl) previewEl.innerHTML = html || '<div style="font-size:12px;color:var(--sub)">Nothing to preview.</div>';
}

// Locate header row (the row containing "Date") within a sheet's row array.
// Mirrors the search logic in parseMatrixSheet. Duplicated from addon.js
// (~5 lines) — DRY candidate for a future micro-refactor; not worth shared.js
// pollution for now.
function findHeaderRow(rows) {
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const r = rows[i];
    if (r && r.some(c => String(c || '').trim().toUpperCase() === 'DATE')) return i;
  }
  return 0;
}

export function acceptSalesSuggestion(store, colIdx) {
  if (!dailyUploadParseState) return;
  dailyUploadParseState.salesAccepted = dailyUploadParseState.salesAccepted.filter(e => e.appName !== store);
  dailyUploadParseState.salesAccepted.push({ idx: colIdx, appName: store });
  rebuildFromState();
}

export function dismissSalesSuggestion(store) {
  if (!dailyUploadParseState) return;
  dailyUploadParseState.salesDismissed.add(store);
  rebuildFromState();
}

export function acceptAddonSuggestion(store, colIdx, headerLabel) {
  if (!dailyUploadParseState) return;
  // Track by header text — sheets can have different column orders
  dailyUploadParseState.addonAccepted = dailyUploadParseState.addonAccepted.filter(e => e.appName !== store);
  dailyUploadParseState.addonAccepted.push({ header: headerLabel, appName: store });
  rebuildFromState();
  showToast('Mapped "' + headerLabel + '" → ' + store, 'success');
}

export function dismissAddonSuggestion(store) {
  if (!dailyUploadParseState) return;
  dailyUploadParseState.addonDismissed.add(store);
  rebuildFromState();
}

export async function save() {
  if (!dailyUploadParseState) return;
  const st = dailyUploadParseState;
  const sales  = st.flatSales  || [];
  const addons = st.flatAddons || [];
  if (!sales.length && !addons.length) {
    showDailyUploadStatus('Nothing to save', 'error'); return;
  }
  showDailyUploadStatus('⏳ Uploading sales (' + sales.length + ') and add-ons (' + addons.length + ')…', 'info');
  // Fire both in parallel; report independently. Don't fail the whole upload
  // if one half fails — the other might've gone through fine.
  const tasks = [];
  if (sales.length)  tasks.push(DB.sales.upload(sales).then(r => ({ kind:'sales',  ok:true, n: sales.length, r })).catch(e => ({ kind:'sales',  ok:false, err:e.message })));
  if (addons.length) tasks.push(DB.addon_actuals.upload(addons).then(r => ({ kind:'addons', ok:true, n: addons.length, r })).catch(e => ({ kind:'addons', ok:false, err:e.message })));
  const results = await Promise.all(tasks);
  const okParts = results.filter(r => r.ok).map(r => '✅ ' + r.kind + ' (' + r.n + ')');
  const errParts = results.filter(r => !r.ok).map(r => '❌ ' + r.kind + ': ' + r.err);
  const allOk = errParts.length === 0;
  showDailyUploadStatus([...okParts, ...errParts].join(' · '), allOk ? 'success' : 'error');
  showToast(allOk ? 'Daily upload complete' : 'Daily upload had errors — see status', allOk ? 'success' : 'error');
  if (allOk) {
    dailyUploadParseState = null;
    const previewCard = document.getElementById('dailyUploadPreviewCard');
    if (previewCard) previewCard.style.display = 'none';
  }
}

export function clearPreview() {
  dailyUploadParseState = null;
  const previewCard = document.getElementById('dailyUploadPreviewCard');
  if (previewCard) previewCard.style.display = 'none';
  const fileInput = document.getElementById('dailyUploadFileInput');
  if (fileInput) fileInput.value = '';
  showDailyUploadStatus('Cancelled.', 'info');
}

function showDailyUploadStatus(msg, type) {
  const el = document.getElementById('dailyUploadStatus');
  const inner = document.getElementById('dailyUploadStatusInner');
  if (!el || !inner) return;
  el.style.display = 'block';
  const colors = {
    success: {bg:'var(--adm-ok-pale)',color:'var(--ok-deep)',border:'var(--adm-ok-line)'},
    error:   {bg:'var(--adm-danger-pale)',color:'var(--danger-deep)',border:'var(--adm-danger-line)'},
    info:    {bg:'var(--info-bg)',color:'var(--adm-sky-text)',border:'var(--adm-info-line)'},
  };
  const c = colors[type] || colors.info;
  inner.style.cssText = `background:${c.bg};color:${c.color};border:1px solid ${c.border};padding:12px 14px;border-radius:10px;font-size:13px`;
  inner.textContent = msg;
}

// ── Daily upload template (v79.7) ───────────────────────
// One workbook with "Daily Sales by Store" + 5 KPI tabs. Identical
// store-column structure to the addon actuals template, with the
// sales tab using the same name as the legacy sales upload expects.
export function downloadTemplate() {
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') {
    ensureXlsx().then(function () { downloadTemplate(); }, function (e) { showToast(e.message, 'error'); }); return;
  }
  const storeSet = new Set(ALL_STORES || []);
  (staffData || []).forEach(s => { if (s.store) storeSet.add(s.store); });
  const appStores = [...storeSet]
    .filter(s => s !== 'Retail Relief Managers' && getStoreType(s) !== 'virtual')
    .sort();
  const storeXlsxNames = appStores.map(s => getStoreUploadName(s));

  const dayNames   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const year = new Date().getFullYear();

  function isoWeek(dt) {
    const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  }
  const metaCols = ['Day', 'Date', 'Month', 'Week'];
  function buildSheetRows(label) {
    const headerRow = [...metaCols, ...storeXlsxNames];
    const placeholderRow = Array(headerRow.length).fill('');
    placeholderRow[0] = label + ' — fill below';
    const rows = [placeholderRow, headerRow];
    let d = new Date(year, 0, 1);
    const endD = new Date(year, 11, 31);
    while (d <= endD) {
      const dateStr = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
      const dayName = dayNames[d.getDay() === 0 ? 6 : d.getDay()-1];
      rows.push([dayName, dateStr, monthNames[d.getMonth()], isoWeek(d), ...storeXlsxNames.map(() => '')]);
      d.setDate(d.getDate() + 1);
    }
    return rows;
  }
  const wb = XLSX.utils.book_new();
  const colWidths = [{wch:11},{wch:12},{wch:10},{wch:6}, ...storeXlsxNames.map(() => ({wch:20}))];
  const sheets = [
    { name: 'Daily Sales by Store', label: 'Sales (currency)' },
    { name: 'Demo',                 label: 'Demo Units (count)' },
    { name: 'Batteries',            label: 'Batteries (count)' },
    { name: 'Gift Wrap',            label: 'Gift Wrap (count)' },
    { name: 'Reusable Bag',         label: 'Reusable Bag (count)' },
    { name: 'Gift Cards',           label: 'Gift Cards (count)' },
  ];
  sheets.forEach(({name, label}) => {
    const ws = XLSX.utils.aoa_to_sheet(buildSheetRows(label));
    ws['!cols'] = colWidths;
    ws['!freeze'] = { xSplit: 4, ySplit: 2 };
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  // README
  const readmeRows = [
    ['Shift — Daily Upload Template (' + year + ')'],
    [''],
    ['Six sheets: Daily Sales by Store + Demo / Batteries / Gift Wrap / Reusable Bag / Gift Cards.'],
    ['Each sheet has the same shape: rows = days, columns = stores.'],
    [''],
    ['Sales: enter the day\'s sales as a currency value (no symbol).'],
    ['Add-on KPIs: enter integer counts of units sold per store per day.'],
    ['Leave a cell blank if no data — blank/zero cells are skipped on upload.'],
    ['Missing tabs are skipped — partial uploads work (e.g. just Sales today,'],
    ['add a Demo column tomorrow, etc.).'],
    [''],
    ['Upload via: Admin → Targets → 📥 Daily Upload.'],
  ];
  const readmeWs = XLSX.utils.aoa_to_sheet(readmeRows);
  readmeWs['!cols'] = [{wch:80}];
  XLSX.utils.book_append_sheet(wb, readmeWs, 'README');
  XLSX.writeFile(wb, 'ELC_Daily_Upload_Template_' + year + '.xlsx');
  showToast('Template downloaded — 6 tabs, ' + appStores.length + ' stores', 'success');
}
